import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { ApiService } from '../api.service';

import { environment as env } from '@env';
import { User } from '@models/user.model';
import { PersonTypes, Registration } from '@models/registration.model';

export const TOKEN_STORAGE_KEY = 'AuthToken';

const REDIRECT_AFTER_AUTH_KEY = 'RedirectAfterAuth';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(private route: ActivatedRoute, private api: ApiService) {}

  async login(username: string, password: string): Promise<{ user: User; token: string }> {
    const res = await this.api.postResource(['Broker', 'Auth'], { body: { username, password } });

    return await this.saveAndReturnAuthData(res);
  }
  async register(data: Registration): Promise<{ user: User; token: string }> {
    const body = {
      cdTipoAnagrafica: data.personType,
      nome: data.firstName,
      cognome: data.lastName,
      ragioneSociale: data.businessName,
      codiceFiscalePIva: data.taxCode,
      dataNascita: data.birthdate ? new Date(data.birthdate).toISOString() : null,
      email: data.email,
      cellulare: data.phone,
      numeroPolizza: data.policyNumber,
      consensoFiniCommerciali: data.privacyPreferences.commercial,
      consensoDati: data.privacyPreferences.data,
      consensoDatiSensibili: data.privacyPreferences.sensibleData,
      consensoMarketing: data.privacyPreferences.marketing,
      consensoCommercialeVersoTerzi: data.privacyPreferences.thirdParty,
      password: data.password
    };
    for (let prop in body) if (body[prop] === undefined || body[prop] === null) delete body[prop];

    const res = await this.api.postResource(['Broker', 'RegistraAutenticaClienti'], { body });

    return await this.saveAndReturnAuthData(res);
  }
  private async saveAndReturnAuthData(authRes: any): Promise<{ user: User; token: string }> {
    if (!authRes?.utente) throw new Error('Bad response format: user');
    const user = new User(authRes.utente);

    const token: string = authRes.utente.token;
    if (!user) throw new Error('Bad response format: token');

    if (env.idea.app.tokenSavedInStorage) localStorage.setItem(TOKEN_STORAGE_KEY, token);

    return { user, token };
  }

  logout(): void {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }

  async forgotPassword(username: string): Promise<void> {
    await this.api.postResource(['Broker', 'ResetPassword'], { body: { username } });
  }
  async resetPassword(newPassword: string, resetCode: string): Promise<void> {
    await this.api.postResource(['Broker', 'ModificaPassword', resetCode], { body: { passwordNew: newPassword } });
  }

  async isAuthenticated(): Promise<{ user: User; token: string }> {
    if (env.idea.app.tokenSavedInStorage) this.api.authToken = localStorage.getItem(TOKEN_STORAGE_KEY);

    if (!this.api.authToken) throw new Error('Missing token');

    // check whether the token is still valid
    const user = new User(await this.api.getResource(['Broker', 'ProfiloUtente']));
    return { user, token: this.api.authToken };
  }

  setRedirectAfterAuthFromQueryParam(): void {
    const redirectPath = this.route.snapshot.queryParamMap.get('redirect');
    if (redirectPath) localStorage.setItem(REDIRECT_AFTER_AUTH_KEY, decodeURIComponent(redirectPath));
  }
  getRedirectPathAfterAuth(): string | null {
    const redirectPath = localStorage.getItem(REDIRECT_AFTER_AUTH_KEY);
    if (redirectPath) localStorage.removeItem(REDIRECT_AFTER_AUTH_KEY);
    return redirectPath ?? null;
  }
}
